import {Component, Injector, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
    AthleteService,
    POS_BUSINESS_CONFIGURATION_ERROR
} from "../../../services/athlete/athlete.service";
import {Athlete} from "../../../models/athlete";
import {TableComponent} from "../../ui/table/table.component";
import {BaseComponent} from "../../base/base.component";
import {CompanyService} from "../../../services/company/company.service";
import {SeasonService} from "../../../services/season/season.service";
import Swal from "sweetalert2";
import {AthleteSeason} from "../../../models/athleteSeason";
import {Company} from "../../../models/company";
import {Subscription} from "rxjs";
import {UserService} from 'src/app/services/user/user.service';
import {PaymentService} from '../../../services/payment/payment.service';
import { StaffService } from "../../../services/staff/staff.service";
import {User} from "../../../models/user";

type DashboardView = 'list' | 'cards';
const DASHBOARD_VIEW_KEY = 'wisper_companyDashboardView';

@Component({
    selector: 'app-dashboard-company',
    templateUrl: './dashboard-company.component.html',
    styleUrls: ['./dashboard-company.component.scss']
})
export class DashboardCompanyComponent extends BaseComponent implements OnInit, OnDestroy {

    @ViewChild('AthletesTable') AthleteTable: TableComponent | undefined;
    @ViewChild('pendingRegistrationConfirmation') pendingRegistrationConfirmationTable: TableComponent | undefined;
    @ViewChild('lastRegistration') lastRegistration: TableComponent | undefined;
    @ViewChild('lastPendingPaymentRegistration') lastPendingPaymentRegistration: TableComponent | undefined;
    @ViewChild('paymentsList') paymentsList: TableComponent | undefined;
    @ViewChild('partialPaymentsList') partialPaymentsList: TableComponent | undefined;
    @ViewChild('staffsTable') staffTable: TableComponent | undefined;

    isCompanyStillPending: boolean = false;
    showSeasonMessage: boolean = false;
    companyId: string = localStorage.getItem('wisper_companyId') ?? ''
    company: Company = new Company()
    
    // Payment status tracking
    paymentStatusSubscription: Subscription | null = null;
    currentPaymentStatus: string = '';
    paymentStatusMessage: string = '';
    showPaymentStatusModal: boolean = false;
    showHelpText: boolean = false;
    helpTextTimeout: any = null;
    currentPaymentId: string = '';
    currentPaymentType: "SINGLE" | "RECURRING" = 'SINGLE';

    paymentMethods: string[] = ['transfer', 'localPayment', 'nexi', 'nexiRates', 'appPagoRates'];
    editingPaymentId: string | null = null;
    editAmount: number = 0;
    athleteTextFilter: string = '';
    selectedPaymentMethod: string | null = '';

    collapseAthletesTable: boolean = true;
    collapseRegistrationConfirmation: boolean = true;
    collapsePendingPayments: boolean = true;
    collapsePaymentsList: boolean = true;
    collapsePartialPayments: boolean = true;
    collapseLastRegistration: boolean = true;
    collapseStaffTable: boolean = true;
    failedInstallmentPayments: number = 0;

    /** Vista della dashboard: 'list' (classica, predefinita) o 'cards' (nuova). Salvata per browser. */
    viewMode: DashboardView = localStorage.getItem(DASHBOARD_VIEW_KEY) === 'cards' ? 'cards' : 'list';
    /** Sezioni della vista a schede espanse oltre i primi elementi. */
    expandedCardSections = new Set<string>();
    readonly cardPreviewSize = 5;

    companies: any[] = [];
    selectedCompany: any = null;
    selectedPayment: any = null;
    selectedStatus: string = 'paid';

    constructor(
        public companyService: CompanyService,
        public seasonService: SeasonService,
        public athleteService: AthleteService,
        private staffService: StaffService,
        public serviceInjector: Injector, public paymentService: PaymentService,
        private userService: UserService,
    ) {
        super(serviceInjector);
    }

    async ngOnInit() {
        let response = await this.companyService.getCompanyById(localStorage.getItem('wisper_companyId') ?? '');

        if (response.success) {
            this.isCompanyStillPending = response.data.status === 'pendingApprovation';
            this.company = response.data;
            this.checkActiveShop();
        }

        let responseSeason = await this.seasonService.getSeasons(localStorage.getItem('wisper_companyId') ?? '');
        if (responseSeason.success) {
            this.showSeasonMessage = responseSeason.data.length === 0;
        }
        this.companies = await this.companyService.getCompanies({ $orderby: 'name asc' });
    }
    ngAfterViewInit() {
        this.AthleteTable?.loadData('/athletes', {
            status: 'pendingCompanyApprovation',
            companyId: localStorage.getItem('wisper_companyId')
        }, (item: any) => {
            return new Athlete(item)
        })

        this.pendingRegistrationConfirmationTable?.loadData('/users', {
            status: 'pendingOtp',
            $orderby: 'createdAt desc',
            companyId: localStorage.getItem('wisper_companyId')
        }, (item: any) => {
            return new Athlete(item)
        })

        this.lastRegistration?.loadData('/recentAthleteSeasons', {
        }, (item: any) => {
            return new AthleteSeason(item)
        })

        this.lastPendingPaymentRegistration?.loadData('/athleteSeasonsToConfirmPayment', {
        }, (item: any) => {
            return new AthleteSeason(item)
        })

        this.staffTable?.loadData('/users', { userGroupId: 'staff', status: 'pendingApprovation' }, (item: any) => {
            return new User(item)
        })

        this.loadPayments();
        this.loadPartialPayments();
    }

    deleteAthlete(athleteId: string) {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler eliminare l'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    this.showSpinner();
                    let athleteResponse = await this.athleteService.deleteAthlete(athleteId);
                    this.hideSpinner();

                    if (athleteResponse.success) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Atleta eliminato con successo'
                        })
                        this.AthleteTable?.reloadPage();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore nell'eliminazione dell'atleta"
                        })
                    }
                }

                if (result.isDismissed) {

                }
            });
    }

    async deletePendingConfirmationUser(userId?: string) {
        if (!userId) {
            await Swal.fire({icon: 'error', title: 'Impossibile identificare l’utente da eliminare'});
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: "Sei sicuro di voler eliminare l'utente?",
            text: 'Questa operazione non può essere annullata',
            showCancelButton: true,
            confirmButtonText: 'Sì, procedi',
            cancelButtonText: "Annulla l'operazione"
        });

        if (!result.isConfirmed) return;

        this.showSpinner();
        let response: any;
        try {
            response = await this.userService.deleteUser(userId);
        } catch (error) {
            console.error('Error deleting pending confirmation user: ', error);
        } finally {
            this.hideSpinner();
        }

        if (response?.success) {
            this.pendingRegistrationConfirmationTable?.reloadPage();
            await Swal.fire({icon: 'success', title: 'Utente eliminato con successo'});
        } else {
            await Swal.fire({icon: 'error', title: "Errore nell'eliminazione dell'utente"});
        }
    }

    approveAthletePayment(paymentId: string) {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler approvare il pagamento in contanti dell'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    let athleteResponse = await this.athleteService.approveAthletePaymentRequest(paymentId);
                    this.spinner.hide();

                    if (athleteResponse) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Pagamento approvato con successo'
                        })
                        this.lastPendingPaymentRegistration?.reloadPage();
                        this.lastRegistration?.reloadPage();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore nell'approvazione del pagamento"
                        })
                    }
                }

                if (result.isDismissed) {

                }
            });
    }
    
    startPaymentPos(paymentId: string, paymentStatus: string) {
        if (paymentStatus === 'STARTED' || paymentStatus === 'SENT' || paymentStatus === 'RECEIVED') {
            this.startListeningPaymentStatus(paymentId);
            return;
        }
        Swal.fire({
            title: "Avvia pagamento POS",
            html: "Scegli il tipo di pagamento da avviare.",
            showCancelButton: true,
            showDenyButton: true,
            showCloseButton: true,
            confirmButtonText: 'Pagamento unico',
            denyButtonText: 'Pagamento a rate',
            denyButtonColor: '#a94ccf',
            cancelButtonText: "Annulla",
            reverseButtons: true,
            customClass: {
                popup: 'wisper-pos-swal',
                confirmButton: 'wisper-pos-swal__primary',
                denyButton: 'wisper-pos-swal__secondary',
                cancelButton: 'wisper-pos-swal__ghost'
            }
        }).then(async (result) => {
            if (result.isConfirmed || result.isDenied) {
                this.spinner.show();
                this.currentPaymentType = result.isDenied ? 'RECURRING' : 'SINGLE';
                let athleteResponse = await this.athleteService.startPaymentPos(paymentId, this.currentPaymentType);
                this.spinner.hide();

                if (this.showPosConfigurationError(athleteResponse)) return;
                
                if (athleteResponse) {
                    // Start listening to payment status
                    this.startListeningPaymentStatus(paymentId);
                }
            }
        });
    }

    startListeningPaymentStatus(paymentId: string) {
        // Unsubscribe from previous subscription if exists
        if (this.paymentStatusSubscription) {
            this.paymentStatusSubscription.unsubscribe();
        }

        // Clear previous help text timeout if exists
        if (this.helpTextTimeout) {
            clearTimeout(this.helpTextTimeout);
            this.helpTextTimeout = null;
        }

        // Reset status
        this.currentPaymentStatus = '';
        this.paymentStatusMessage = '';
        this.showPaymentStatusModal = true;
        this.showHelpText = false;
        this.currentPaymentId = paymentId;

        // Open the modal and add centered class
        const modal = this.ngxSmartModalService.getModal('paymentStatusModal');
        if (modal) {
            modal.open();
            // Add centered class to dialog after modal opens
            setTimeout(() => {
                const dialog = document.querySelector('ngx-smart-modal[identifier="paymentStatusModal"] .nsm-dialog');
                if (dialog) {
                    dialog.classList.add('nsm-centered');
                }
            }, 100);
        }

        // Set timeout to show help text after 30 seconds
        this.helpTextTimeout = setTimeout(() => {
            if (this.currentPaymentStatus !== 'PROCESSED' && this.currentPaymentStatus !== 'CANCELED' && this.currentPaymentStatus !== 'ERROR') {
                this.showHelpText = true;
            }
        }, 30000);

        // Subscribe to status updates
        this.paymentStatusSubscription = this.athleteService.listenStatusPaymentPos(paymentId).subscribe({
            next: (status: any) => {
                console.log('Payment status update:', status);
                this.currentPaymentStatus = status.status || '';
                //to permit to read the status message
                this.updatePaymentStatusMessage(status.status);

                // If payment is processed or canceled, reload tables and close modal after delay
                if (status.status === "PROCESSED") {
                    // Clear help text timeout
                    if (this.helpTextTimeout) {
                        clearTimeout(this.helpTextTimeout);
                        this.helpTextTimeout = null;
                    }
                    this.showHelpText = false;
                    this.lastPendingPaymentRegistration?.reloadPage();
                    setTimeout(() => {
                        this.closePaymentStatusModal();
                        this.lastRegistration?.reloadPage();
                        this.paymentsList?.reloadPage();
                        this.loadPartialPayments();
                    }, 5000);
                } else if (status.status === "CANCELED") {
                    // Clear help text timeout
                    if (this.helpTextTimeout) {
                        clearTimeout(this.helpTextTimeout);
                        this.helpTextTimeout = null;
                    }
                    this.showHelpText = false;
                    setTimeout(() => {
                        this.closePaymentStatusModal();
                        this.lastRegistration?.reloadPage();
                    }, 5000);
                }
            },
            error: (error) => {
                console.error('Error listening to payment status:', error);
                // Clear help text timeout
                if (this.helpTextTimeout) {
                    clearTimeout(this.helpTextTimeout);
                    this.helpTextTimeout = null;
                }
                this.showHelpText = false;
                this.currentPaymentStatus = 'ERROR';
                this.paymentStatusMessage = 'Errore nel monitoraggio del pagamento';
                setTimeout(() => {
                    this.closePaymentStatusModal();
                }, 3000);
            },
            complete: () => {
                console.log('Payment status stream completed');
                // Stream completed, close modal after delay if not already closed
                setTimeout(() => {
                    this.closePaymentStatusModal();
                }, 2000);
            }
        });
    }

    updatePaymentStatusMessage(status: string) {
        switch (status) {
            case "STARTED":
                this.paymentStatusMessage = 'Pagamento avviato';
                break;
            case "SENT":
                this.paymentStatusMessage = 'Connessione al POS in corso…';
                break;
            case "RECEIVED":
                this.paymentStatusMessage = 'POS collegato correttamente';
                break;
            case "PROCESSED":
                this.paymentStatusMessage = 'Pagamento completato';
                break;
            case "CANCELED":
                this.paymentStatusMessage = 'Pagamento annullato';
                break;
            default:
                this.paymentStatusMessage = 'Monitoraggio pagamento in corso...';
        }
    }

    getPaymentStatusLabel(status?: string | null): string {
        switch (status) {
            case "STARTED":
            case "SENT":
            case "RECEIVED":
                return 'In corso';
            case "CANCELED":
            case "ERROR":
                return 'Riprova';
            case "PROCESSED":
                return 'Pos';
            default:
                return 'Pos';
        }
    }


    getStatusClass(): string {
        switch (this.currentPaymentStatus) {
            case "PROCESSED":
                return 'status-success';
            case "CANCELED":
            case "ERROR":
                return 'status-error';
            case "RECEIVED":
                return 'status-warning';
            default:
                return 'status-info';
        }
    }

    getModalStatusLabel(status?: string | null): string {
        switch (status) {
            case "PROCESSED":
                return 'Completato';
            case "CANCELED":
                return 'Annullato';
            case "ERROR":
                return 'Errore';
            case "STARTED":
            case "SENT":
            case "RECEIVED":
            default:
                return 'In corso';
        }
    }

    // getStatusBadgeClass(): string {
    //     switch (this.currentPaymentStatus) {
    //         case "PROCESSED":
    //             return 'badge-success';
    //         case "CANCELED":
    //         case "ERROR":
    //             return 'badge-error';
    //         case "RECEIVED":
    //             return 'badge-warning';
    //         default:
    //             return 'badge-info';
    //     }
    // }

    showHelpPaymentPosPopUp() {
        this.ngxSmartModalService.getModal('helpPaymentPosPopUp')?.open();
    }

    async retryPaymentPos() {
        if (!this.currentPaymentId) return;
        
        // Reset help text
        this.showHelpText = false;
        
        // Clear previous subscription and timeout
        if (this.paymentStatusSubscription) {
            this.paymentStatusSubscription.unsubscribe();
            this.paymentStatusSubscription = null;
        }
        if (this.helpTextTimeout) {
            clearTimeout(this.helpTextTimeout);
            this.helpTextTimeout = null;
        }
        
        // Reset status
        this.currentPaymentStatus = '';
        this.paymentStatusMessage = 'Riprovo il pagamento...';
        
        // Retry the payment directly without confirmation
        this.spinner.show();
        let athleteResponse = await this.athleteService.startPaymentPos(this.currentPaymentId, this.currentPaymentType);
        this.spinner.hide();

        if (this.showPosConfigurationError(athleteResponse)) return;
        
        if (athleteResponse) {
            // Start listening to payment status again
            this.startListeningPaymentStatus(this.currentPaymentId);
        }
    }

    private showPosConfigurationError(response: any): boolean {
        if (response?.error !== POS_BUSINESS_CONFIGURATION_ERROR) return false;

        Swal.fire({
            icon: 'error',
            title: 'Errore configurazione POS',
            text: 'Controlla la configurazione Business ID'
        });
        return true;
    }

    onPaymentStatusModalClose() {
        // Clean up subscription when modal is closed
        if (this.paymentStatusSubscription) {
            this.paymentStatusSubscription.unsubscribe();
            this.paymentStatusSubscription = null;
        }
        // Clear help text timeout
        if (this.helpTextTimeout) {
            clearTimeout(this.helpTextTimeout);
            this.helpTextTimeout = null;
        }
        this.showPaymentStatusModal = false;
        this.currentPaymentStatus = '';
        this.paymentStatusMessage = '';
        this.showHelpText = false;
        this.currentPaymentId = '';
    }

    closePaymentStatusModal() {
        if (this.paymentStatusSubscription) {
            this.paymentStatusSubscription.unsubscribe();
            this.paymentStatusSubscription = null;
        }
        // Clear help text timeout
        if (this.helpTextTimeout) {
            clearTimeout(this.helpTextTimeout);
            this.helpTextTimeout = null;
        }
        this.showPaymentStatusModal = false;
        this.ngxSmartModalService.getModal('paymentStatusModal')?.close();
        this.currentPaymentStatus = '';
        this.paymentStatusMessage = '';
        this.showHelpText = false;
        this.currentPaymentId = '';
    }

    ngOnDestroy() {
        if (this.paymentStatusSubscription) {
            this.paymentStatusSubscription.unsubscribe();
        }
        // Clear help text timeout
        if (this.helpTextTimeout) {
            clearTimeout(this.helpTextTimeout);
            this.helpTextTimeout = null;
        }
    }
    rejectAthletePayment(paymentId: string) {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler rifiutare il pagamento dell'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    let athleteResponse = await this.athleteService.rejectAthletePaymentRequest(paymentId);
                    this.spinner.hide();

                    if (athleteResponse) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Pagamento respinto con successo'
                        })
                        this.lastPendingPaymentRegistration?.reloadPage();
                        this.lastRegistration?.reloadPage();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore, non è stato possibile respingere il pagamento"
                        })
                    }
                }

                if (result.isDismissed) {

                }
            });
    }

    // ---- Vista a schede -------------------------------------------------------

    setViewMode(mode: DashboardView) {
        this.viewMode = mode;
        localStorage.setItem(DASHBOARD_VIEW_KEY, mode);
    }

    get pendingAthleteRows(): any[] { return this.AthleteTable?.rows ?? []; }
    get pendingPaymentRows(): any[] { return this.lastPendingPaymentRegistration?.rows ?? []; }
    get partialPaymentRows(): any[] { return this.partialPaymentsList?.rows ?? []; }
    get recentRegistrationRows(): any[] { return this.lastRegistration?.rows ?? []; }
    get pendingStaffRows(): any[] { return this.staffTable?.rows ?? []; }
    get paymentRows(): any[] { return this.paymentsList?.rows ?? []; }

    /** Totale da backend se disponibile, altrimenti le righe caricate. */
    countOf(table: TableComponent | undefined): number {
        return table ? (table.totalCount || table.rows.length) : 0;
    }

    visibleRows(section: string, rows: any[]): any[] {
        return this.expandedCardSections.has(section) ? rows : rows.slice(0, this.cardPreviewSize);
    }

    toggleCardSection(section: string) {
        if (this.expandedCardSections.has(section)) this.expandedCardSections.delete(section);
        else this.expandedCardSections.add(section);
    }

    scrollToCard(id: string) {
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    initials(firstName?: string, lastName?: string): string {
        return `${(firstName ?? '').trim().charAt(0)}${(lastName ?? '').trim().charAt(0)}`.toUpperCase() || '?';
    }

    isPosInProgress(status?: string | null): boolean {
        return status === 'STARTED' || status === 'SENT' || status === 'RECEIVED';
    }

    requestStatus(status?: string): { label: string; classes: string } {
        switch (status) {
            case 'paid': return {label: 'Pagato', classes: 'bg-emerald-50 text-emerald-700'};
            case 'partialPayment': return {label: 'Parziale', classes: 'bg-sky-50 text-sky-800'};
            case 'pendingPayment': return {label: 'In attesa', classes: 'bg-amber-50 text-amber-800'};
            case 'verifyingPayment': return {label: 'In verifica', classes: 'bg-amber-50 text-amber-800'};
            case 'notPaid':
            case 'paymentFailed': return {label: 'Errore', classes: 'bg-red-50 text-red-700'};
            default: return {label: status || '—', classes: 'bg-slate-100 text-slate-600'};
        }
    }

    /** Data di nascita formattata, oppure '' se mancante o non valida (evita "Invalid date"). */
    bornDateLabel(value?: string): string {
        if (!value) return '';
        const label = this.formatDateForTable(value);
        return label && !/invalid/i.test(label) ? label : '';
    }

    installmentProgress(row: any): number {
        const total = Number(row.totalInstallments) || 0;
        return total ? Math.min(100, Math.round((Number(row.completedInstallments) || 0) / total * 100)) : 0;
    }

    checkActiveShop(): boolean {
        const activeService = this.utils.getJwtCompanyServices()?.['onlineshop'];
        return activeService === 'onlineshop';
    }


    async loadPayments() {
        const params: any = {};
        if (this.selectedStatus) params['status'] = this.selectedStatus;

        console.log("selectedCompany: ", this.selectedCompany)
        if (!this.isSuperAdmin()) {
            params['companyId'] = this.companyId;
        } else if (this.selectedCompany && this.selectedCompany[0]?.id) {
            params['companyId'] = this.selectedCompany[0]?.id;
        }
        if (this.athleteTextFilter) params['athleteText'] = this.athleteTextFilter;
        if (this.selectedPaymentMethod) params['paymentMethod'] = this.selectedPaymentMethod;

        this.paymentsList?.loadData(
            `/allAthletePaymentRequests`,
            params,
            (item: any) => item,
            true
        );
        console.log("lista pagamenti ->", this.paymentsList);
    }

    async loadPartialPayments() {
        const params: any = {
            status: 'partialPayment'
        };

        if (!this.isSuperAdmin()) {
            params['companyId'] = this.companyId;
        } else if (this.selectedCompany && this.selectedCompany[0]?.id) {
            params['companyId'] = this.selectedCompany[0]?.id;
        }

        await this.partialPaymentsList?.loadData(
            `/allAthletePaymentRequests`,
            params,
            (item: any) => item,
            true
        );
        this.failedInstallmentPayments = (this.partialPaymentsList?.rows ?? [])
            .filter((row: any) => row.paymentIssue).length;
    }

    showPartialPayments() {
        this.collapsePartialPayments = false;
        document.getElementById('partial-payments')?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    filterSearch() {
        this.loadPayments();
    }

    async deletePayment(paymentId: string) {
        const result = await Swal.fire({
            icon: 'warning',
            title: "Sei sicuro di voler eliminare il pagamento?",
            text: "Non si potrà più tornare indietro",
            showCancelButton: true,
            confirmButtonText: "Sì, procedi",
            cancelButtonText: "No, annulla"
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            this.showSpinner();

            const response = await this.athleteService.deletePaymentRequest(paymentId);

            if (response?.success) {
                this.showSuccessMessage('Pagamento eliminato con successo');
                this.loadPayments();
            } else {
                this.showErrorMessage(
                    response?.error || "Errore durante l'eliminazione del pagamento"
                );
            }

        } catch (error) {
            this.showErrorMessage("Errore imprevisto durante l'eliminazione");
        } finally {
            this.hideSpinner();
        }
    }

    canEdit(payment: any): boolean {
        console.log("canEdit payment: ", payment)
        return payment.paymentMethod === 'transfer' || payment.paymentMethod === 'localPayment';
    }

    startEdit(payment: any) {
        if (!this.canEdit(payment)) return;

        this.editingPaymentId = payment.id;
        this.editAmount = payment.amount;
    }

    cancelEdit() {
        this.editingPaymentId = null;
        this.editAmount = 0;
    }

    getPaymentMethodLabel(method: string): string {
        switch (method) {
            case 'transfer':
                return 'Bonifico';
            case 'localPayment':
                return 'Pagamento in segreteria';
            case 'nexi':
                return 'Carta di Credito (Nexi)';
            case 'nexiRates':
                return 'Rateale (Nexi)';
            case 'appPagoRates':
                return 'Rateale (AppPago)';
            default:
                return method;
        }
    }

    resetFilters() {
        this.athleteTextFilter = '';
        this.selectedPaymentMethod = '';
        this.selectedCompany = null;
        this.selectedStatus = 'paid';
        this.filterSearch();
    }

    companySettings = {
        singleSelection: true,
        idField: 'id',
        textField: 'name',
        allowSearchFilter: true,
        closeDropDownOnSelection: true,
        clearSearchFilter: true
    }

    openEditModal(payment: any) {
        this.selectedPayment = payment;
        this.editAmount = payment.amount;
    }

    closeEditModal() {
        this.selectedPayment = null;
        this.editAmount = 0;
    }

    async confirmEdit() {
        if (this.editAmount <= 0) {
            this.showErrorMessage("L'importo deve essere maggiore di zero");
            return;
        }
        try {
            this.showSpinner();
            const response = await this.paymentService.updatePaymentAmount(this.selectedPayment.id, this.editAmount, 'paid');

            if (response?.success) {
                this.showSuccessMessage("Importo aggiornato con successo");
                this.closeEditModal();
                this.loadPayments();
            } else {
                this.showErrorMessage(response?.error || "Errore durante l'aggiornamento");
            }

        } catch (error) {
            this.showErrorMessage("Errore imprevisto");
        } finally {
            this.hideSpinner();
        }
    }

    async approveStaff(staffId: string) {
        console.log("approveStaff ->", staffId)
        this.staffService.approve(staffId).subscribe((response: any) => {
            if (response.success) {
                this.showSuccessMessage('Staff approvato con successo!')
                this.staffTable?.reloadPage();
            } else {
                this.showErrorMessage('Errore durante l\'approvazione dello staff')
            }
        });
    }

    async rejectStaff(staffId: string) {
        this.staffService.reject(staffId).subscribe((response: any) => {
            if (response.success) {
                this.showSuccessMessage('Staff rifiutato!')
                this.staffTable?.reloadPage();
            } else {
                this.showErrorMessage('Errore, impossibile rifiutare lo staff')
            }
        });
    }
}
